// API Configuration for THE MASTERMIND COACHING CLASSES
// Change this URL to connect to different backend environments

// API Base URL - reads from VITE_API_BASE_URL env var, falls back to '/api' for nginx proxy
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Secondary/fallback API base URL - used automatically when the primary API
// (App Service) is unreachable, e.g. during a quota outage. Points at the
// standing Container Apps deployment, which shares the same JWT secret and
// database connection as production so an in-flight session stays valid.
// Only takes effect when both env vars are set to distinct absolute URLs.
export const SECONDARY_API_BASE_URL = import.meta.env.VITE_SECONDARY_API_BASE_URL || ''

// API Endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    REQUEST_OTP: '/auth/otp/request',
    VERIFY_OTP: '/auth/otp/verify',
    REFRESH_TOKEN: '/auth/token/refresh',
    LOGOUT: '/auth/logout',
    CURRENT_USER: '/auth/me'
  },
  
  // Dashboard
  DASHBOARD: {
    PARENT_STATS: '/dashboard/parent-stats',
    TEACHER_STATS: '/dashboard/teacher-stats',
    ADMIN_STATS: '/dashboard/admin-stats',
    RECENT_STUDENTS: '/dashboard/recent-students'
  },

  // Parent
  PARENT: {
    BASE: '/parent',
    CHILDREN: '/parent/children',
    DASHBOARD_STATS: '/parent/dashboard/stats'
  },
  
  // Students
  STUDENTS: {
    LIST: '/students',
    CREATE: '/students',
    UPDATE: (id: string) => `/students/${id}`,
    DELETE: (id: string) => `/students/${id}`,
    ATTENDANCE: '/students/attendance',
    PERFORMANCE: (id: string) => `/students/${id}/performance`,
    REMARKS: (id: string) => `/students/${id}/remarks`
  },
  
  // Teachers
  TEACHERS: {
    LIST: '/teachers',
    CREATE: '/teachers',
    UPDATE: (id: string) => `/teachers/${id}`,
    DELETE: (id: string) => `/teachers/${id}`,
    PHOTO: (id: string) => `/teachers/${id}/photo`,
    INVITATION: (id: string) => `/teachers/${id}/invitation`,
    SCHEDULE: '/teachers/schedule',
    CLASSES: '/teachers/classes'
  },
  
  // Classes
  CLASSES: {
    LIST: '/classes',
    CREATE: '/classes',
    UPDATE: (id: string) => `/classes/${id}`,
    DELETE: (id: string) => `/classes/${id}`,
    ATTENDANCE: (id: string) => `/classes/${id}/attendance`,
    SUBJECTS: (id: string) => `/classes/${id}/subjects`
  },
  
  // Attendance
  ATTENDANCE: {
    MARK: '/attendance/mark',
    REPORT: '/attendance/report',
    SUMMARY: '/attendance/summary',
    HISTORY: '/attendance/history'
  },
  
  // Fees
  FEES: {
    STRUCTURE: '/fees/structure',
    PAYMENT: '/fees/payment',
    HISTORY: '/fees/history',
    PENDING: '/fees/pending',
    INVOICE: (id: string) => `/fees/invoice/${id}`
  },
  
  // Subjects
  SUBJECTS: {
    LIST: '/subjects',
    CREATE: '/subjects',
    UPDATE: (id: string) => `/subjects/${id}`,
    DELETE: (id: string) => `/subjects/${id}`,
    ASSIGN: '/subjects/assign'
  },
  
  // Sessions
  SESSIONS: {
    LIST: '/sessions',
    CREATE: '/sessions',
    UPDATE: (id: string) => `/sessions/${id}`,
    DELETE: (id: string) => `/sessions/${id}`,
    ACTIVATE: '/sessions',
    ACTIVE: '/sessions/active'
  },
  
  // Reports
  REPORTS: {
    PERFORMANCE: '/reports/performance',
    ATTENDANCE: '/reports/attendance',
    FINANCIAL: '/reports/financial',
    ACADEMIC: '/reports/academic'
  },
  
  // Notifications
  NOTIFICATIONS: {
    LIST: '/notifications',
    SEND: '/notifications/send',
    MARK_READ: (id: string) => `/notifications/${id}/read`,
    PREFERENCES: '/notifications/preferences'
  }
}

// Default timeout for API requests (in milliseconds)
export const API_TIMEOUT = 30000

// Request headers configuration
export const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
}

// Environment detection
export const isDevelopment = import.meta.env.DEV
export const isProduction = import.meta.env.PROD

// Helper function to get full API URL
export const getApiUrl = (endpoint: string): string => {
  return `${API_BASE_URL}${endpoint}`
}

// Helper function to check if URL is absolute
export const isAbsoluteUrl = (url: string): boolean => {
  return /^https?:\/\//.test(url)
}

// Helper function to get image URL
export const getImageUrl = (path: string): string => {
  if (isAbsoluteUrl(path)) return path
  return `${API_BASE_URL}/uploads/${path}`
}
